// src/components/SearchBar.js
import React, { useRef, useEffect, useCallback } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { debounce } from 'lodash'; // Using lodash for debouncing

const libraries = ['places'];

// Sanitize input function
function sanitizeInput(input) {
  if (!input) return '';
  return input.replace(/[''`′]/g, "'").replace(/[""]/g, '"');
}

const SearchBar = ({ onPlaceSelected, value, onChange, placeholder }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  // Extract zip and city functions remain the same
  const getZipCode = (addressComponents) => {
    const postalComponent = addressComponents.find((c) =>
      c.types.includes('postal_code')
    );
    return postalComponent ? postalComponent.long_name : null;
  };

  const getCity = (addressComponents) => {
    const cityComponent = addressComponents.find((c) =>
      c.types.includes('locality') ||
      c.types.includes('sublocality') ||
      c.types.includes('administrative_area_level_2')
    );
    return cityComponent ? cityComponent.long_name : null;
  };

  // Initialize autocomplete only once
  useEffect(() => {
    if (isLoaded && inputRef.current && !autocompleteRef.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current
      );

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place.place_id) {
          const placeDetails = {
            placeId: place.place_id,
            name: place.name,
            address: place.formatted_address,
            latitude: place.geometry?.location?.lat(),
            longitude: place.geometry?.location?.lng(),
            zipCode: getZipCode(place.address_components),
            city: getCity(place.address_components),
          };
          onPlaceSelected(placeDetails);
        }
      });
    }
  }, [isLoaded, onPlaceSelected]);

  // Debounced handler for input changes
  const debouncedInputChange = useCallback(
    debounce((value) => {
      if (autocompleteRef.current && value.length >= 3) {
        // Just set the value and let Google handle the autocomplete
        inputRef.current.value = value;
        
        // Trigger a DOM input event to make the autocomplete update
        const event = new Event('input', { bubbles: true });
        inputRef.current.dispatchEvent(event);
      }
    }, 300), // 300ms debounce
    []
  );

  const handleChange = (e) => {
    const sanitized = sanitizeInput(e.target.value);
    
    // Update the parent component's state
    onChange({
      ...e,
      target: {
        ...e.target,
        value: sanitized,
      },
    });
    
    // Trigger debounced autocomplete
    debouncedInputChange(sanitized);
  };

  return (
    <div className="search-bar-container">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        autoComplete="off" // Disable browser's native autocomplete
      />
    </div>
  );
};

export default SearchBar;